import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { encryptStorage } from '../utils/storage';
import localCache from '../utils/localCache';
import { STORAGE_KEYS } from '../utils/storageKeys';

// Broadcast auth failures out of this non-React module. AuthErrorHandler
// listens for the event and owns the error toast plus the client-side
// /sign_in redirect, so the redirect no longer races (and destroys) the
// rejected response the way a synchronous window.location.replace did.
export const AUTH_ERROR_EVENT = 'auth-error';

// No default Authorization header: a token baked in at load time outlives a
// logout or 401 in another tab, so every request must read storage instead.
const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });

// Function to check if a token is expired
const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    // Get the current time in seconds (JWT exp is in seconds)
    const currentTime = Date.now() / 1000;

    // Return true if the token is expired
    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

const emitAuthError = (status) => {
  window.dispatchEvent(
    new CustomEvent(AUTH_ERROR_EVENT, { detail: { status } }),
  );
};

const endSession = () => {
  encryptStorage.removeItem('authToken');
  encryptStorage.removeItem('userSession');
  // Otherwise the pre-paint script keeps painting the expired session's palette.
  localCache.remove(STORAGE_KEYS.RESOLVED_THEME);
};

// React Router also matches the trailing-slash form of the route.
const onSignInPage = () =>
  window.location.pathname.replace(/\/+$/, '') === '/sign_in';

api.interceptors.request.use(async (request) => {
  const authToken = encryptStorage.getItem('authToken');
  request.headers['Content-Type'] = 'application/vnd.api+json';

  // A missing token is treated like an expired one: outside /sign_in every
  // request needs auth, so abort it and let AuthErrorHandler redirect.
  if (isTokenExpired(authToken)) {
    endSession();

    if (!onSignInPage()) {
      emitAuthError(401);
      throw new Error('Session expired. Please sign in again.');
    }
  } else {
    request.headers.Authorization = authToken;
  }
  return request;
});

// Add a response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    // Only 401 (unauthenticated / invalid session) ends the session. A 403 is
    // an authorization denial for an otherwise-valid session (e.g. a non-admin
    // hitting an admin-only endpoint) and must surface as an error to the
    // caller, not log the user out.
    if (status === 401 && !onSignInPage()) {
      endSession();
      emitAuthError(401);
    } else if (status === 403) {
      emitAuthError(403);
    }
    return Promise.reject(error);
  },
);
export default api;
